import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type VoiceCall } from "@/lib/dashApi";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, KpiCard, LoadingState, PageHeader, Panel } from "@/components/dash/ui";

const VIEWS = [
  { key: "all", label: "All calls" },
  { key: "missed", label: "Missed" },
  { key: "unacknowledged", label: "Missed · unacked" },
  { key: "voicemail", label: "Voicemail" },
  { key: "outbound", label: "Outbound" },
];

const PAGE_SIZE = 50;

function fmtSecs(s: number | null): string {
  if (s == null || s <= 0) return "—";
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

function CallBadges({ c }: { c: VoiceCall }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {c.missed && !c.acknowledged && !c.callback_logged && (
        <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-medium text-rose-300">unacked</span>
      )}
      {c.missed && (c.acknowledged || c.callback_logged) && (
        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">missed · handled</span>
      )}
      {c.voicemail && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300">voicemail</span>}
      {c.followup_state === "open" && (
        <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300">
          follow-up{c.followup_due_at ? ` ${fmtRelative(c.followup_due_at)}` : ""}
        </span>
      )}
      {c.has_recording && (
        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">🎙 recording</span>
      )}
    </span>
  );
}

function TranscriptView({ callId }: { callId: string }) {
  const q = useQuery({ queryKey: ["voice-call", callId], queryFn: () => dashApi.voiceCallDetail(callId) });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const d = q.data;
  if (!d) return null;
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400 md:grid-cols-4">
        <div><dt className="inline">Status:</dt> <dd className="inline text-slate-200">{d.status}</dd></div>
        <div><dt className="inline">Duration:</dt> <dd className="inline text-slate-200">{fmtSecs(d.duration_seconds)}</dd></div>
        <div><dt className="inline">Disposition:</dt> <dd className="inline text-slate-200">{d.disposition ?? "—"}</dd></div>
        <div><dt className="inline">Assigned:</dt> <dd className="inline text-slate-200">{d.assigned_to ?? "—"}</dd></div>
      </dl>
      {d.disposition_note && (
        <p className="rounded bg-ink-800 px-3 py-2 text-xs text-slate-300">Note: {d.disposition_note}</p>
      )}
      {d.transcript.length ? (
        <ol className="max-h-96 space-y-2 overflow-y-auto rounded-lg bg-ink-800/70 p-3">
          {d.transcript.map((line, i) => (
            <li key={i} className="text-sm">
              <span className={
                line.speaker === "customer" ? "text-brand-300" :
                line.speaker === "ai" ? "text-violet-300" :
                line.speaker === "staff" ? "text-emerald-300" : "text-slate-500"
              }>
                {line.speaker}
              </span>
              <span className="text-slate-600"> · </span>
              <span className="text-slate-200">{line.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState compact>
          {d.transcript_state === "pending" ? "Transcript is still being generated in IronChat." :
           d.transcript_state === "failed" ? "Transcription failed in IronChat." :
           "No transcript for this call (transcripts exist for voicemail and AI-handled calls)."}
        </EmptyState>
      )}
      {d.has_recording && (
        <p className="text-[11px] text-slate-500">
          🎙 A recording exists for this call — audio is not copied into the dashboard.{" "}
          <a href="https://chat.bgr.bz/calls" target="_blank" rel="noreferrer" className="text-brand-300 hover:text-brand-200">
            Listen in IronChat →
          </a>
        </p>
      )}
    </div>
  );
}

export default function VoiceCallsPage() {
  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ["voice-summary"], queryFn: dashApi.voiceSummary });
  const calls = useQuery({
    queryKey: ["voice-calls", view, q, offset],
    queryFn: () => dashApi.voiceCalls(view, q || undefined, PAGE_SIZE, offset),
  });
  const s = summary.data;
  const d = calls.data;

  const pick = (v: string) => { setView(v); setOffset(0); setSelected(null); };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Calls & Voice"
        description="IronChat call log, missed-call queue and transcripts, mirrored read-only into the warehouse. Admin-only — rows carry caller numbers and transcript text."
        right={s?.last_sync_at ? <span className="text-xs text-slate-500">synced {fmtRelative(s.last_sync_at)}</span> : undefined}
      />

      {s && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          <KpiCard label="Calls (all time)" value={s.calls_total} unit="count" />
          <KpiCard label="Missed" value={s.missed_total} unit="count" />
          <KpiCard label="Missed unacked" value={s.missed_unacknowledged} unit="count" tone={s.missed_unacknowledged ? "danger" : undefined} />
          <KpiCard label="Voicemails" value={s.voicemail_total} unit="count" />
          <KpiCard label="With transcript" value={s.with_transcript} unit="count" />
          <KpiCard label="Open follow-ups" value={s.followups_open} unit="count" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" onClick={() => pick(v.key)}
            className={`rounded-lg border px-3 py-1.5 text-xs ${view === v.key
              ? "border-brand-400/60 bg-brand-500/15 text-brand-200"
              : "border-ink-500/60 bg-ink-800 text-slate-300 hover:bg-ink-700"}`}>
            {v.label}
          </button>
        ))}
        <form className="ml-auto" onSubmit={(e) => { e.preventDefault(); setQ(search.trim()); setOffset(0); }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number / name…"
            className="w-56 rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600" />
        </form>
      </div>

      <Panel title={`Call log${d ? ` (${d.total})` : ""}`} subtitle="Click a row to open the call detail and transcript.">
        {calls.isLoading ? <LoadingState /> : calls.isError ? <ErrorState error={calls.error} /> : !d || !d.items.length ? (
          <EmptyState>No calls match this view yet. The log fills on the next connector sweep (every ~10 minutes).</EmptyState>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">When</th>
                  <th className="pb-2 font-medium">Caller</th>
                  <th className="pb-2 font-medium">Direction</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 text-right font-medium">Duration</th>
                  <th className="pb-2 font-medium">Flags</th>
                  <th className="pb-2 text-right font-medium">Transcript</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {d.items.map((c) => (
                  <tr key={c.id}
                    onClick={() => setSelected(selected === c.id ? null : c.id)}
                    className={`cursor-pointer text-slate-300 hover:bg-ink-700/40 ${selected === c.id ? "bg-ink-700/60" : ""}`}>
                    <td className="py-2 text-[12px] text-slate-400" title={fmtDateTime(c.occurred_at)}>{fmtRelative(c.occurred_at)}</td>
                    <td className="py-2">
                      <span className="text-slate-100">{c.customer_name ?? (c.direction === "outbound" ? c.to_number : c.from_number) ?? "unknown"}</span>
                      {c.customer_name && (
                        <div className="text-[11px] text-slate-600">{c.direction === "outbound" ? c.to_number : c.from_number}</div>
                      )}
                    </td>
                    <td className="py-2 text-[12px]">{c.direction === "outbound" ? "↗ outbound" : "↙ inbound"}</td>
                    <td className="py-2 text-[12px]">{c.status}</td>
                    <td className="py-2 text-right tabular-nums text-[12px]">{fmtSecs(c.duration_seconds)}</td>
                    <td className="py-2"><CallBadges c={c} /></td>
                    <td className="py-2 text-right text-[12px]">{c.has_transcript ? "📄 view" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="rounded border border-ink-500/60 px-2 py-1 disabled:opacity-40">← Newer</button>
                <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, d.total)} of {d.total}</span>
                <button type="button" disabled={offset + PAGE_SIZE >= d.total} onClick={() => setOffset(offset + PAGE_SIZE)}
                  className="rounded border border-ink-500/60 px-2 py-1 disabled:opacity-40">Older →</button>
              </div>
            )}
          </>
        )}
      </Panel>

      {selected && (
        <div key={selected} ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "nearest" })}>
          <Panel title="Call detail & transcript">
            <TranscriptView callId={selected} />
          </Panel>
        </div>
      )}
      <FeedbackWidget targetType="page" targetRef="/voice/calls" />
    </div>
  );
}
